import { useState, useEffect } from "react";
import { Menu, X, Sparkles, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { scrollToSection } from "@/utils/scrollUtils";

const LP1790Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScrollToSection = (sectionId: string) => {
    scrollToSection(sectionId);
    setIsMenuOpen(false);
  };

  const navigationItems = [
    { label: "Início", id: "hero" },
    { label: "Sobre", id: "sobre", link: "/#sobre" },
    { label: "Como Funciona", id: "como-funciona", link: "/#como-funciona" },
    { label: "Preço", id: "precos" },
    { label: "Depoimentos", id: "depoimentos" },
    { label: "FAQ", id: "faq" },
    { label: "Blog", id: "blog", link: "/blog" }
  ];

  return (
    <>
      <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'glass-effect border-b border-dark-border' : 'bg-dark-surface backdrop-blur-sm'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <button onClick={() => handleScrollToSection('hero')}>
                <img 
                  alt="AuraCode Logo" 
                  src="/lovable-uploads/b922e941-ba23-47fb-be9f-50db1c876345.png" 
                  className="h-20 w-auto filter brightness-0 invert"
                  width={320}
                  height={80}
                  decoding="async"
                />
              </button>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              {navigationItems.map((item) => (
                item.link ? (
                  <Link
                    key={item.id}
                    to={item.link}
                    className="text-gray-300 hover:text-dark-accent transition-colors duration-200 font-medium"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <button
                    key={item.id}
                    onClick={() => handleScrollToSection(item.id)}
                    className="text-gray-300 hover:text-dark-accent transition-colors duration-200 font-medium"
                  >
                    {item.label}
                  </button>
                )
              ))}
              
              <Button asChild variant="outline" className="border-gray-600 text-gray-700 hover:bg-brand-secondary hover:text-white">
                <Link to="/app/login">
                  <LogIn className="mr-2 h-4 w-4" />
                  Login
                </Link>
              </Button>
              
              <Button 
                className="bg-moss-green hover:bg-moss-green/90 text-white font-semibold hover-scale animate-pulse" 
                onClick={() => handleScrollToSection('precos')}
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Gerar Mapa
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-white hover:text-dark-accent transition-colors" 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsMenuOpen(false)} />
          <div className="absolute top-20 left-0 right-0 glass-effect border-b border-dark-border">
            <nav className="container mx-auto px-4 py-6 space-y-4">
              {navigationItems.map((item) => (
                item.link ? (
                  <Link
                    key={item.id}
                    to={item.link}
                    className="block w-full text-left py-3 px-4 text-gray-300 hover:text-dark-accent hover:bg-dark-surface/50 rounded-lg transition-all duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <button
                    key={item.id}
                    onClick={() => handleScrollToSection(item.id)}
                    className="block w-full text-left py-3 px-4 text-gray-300 hover:text-dark-accent hover:bg-dark-surface/50 rounded-lg transition-all duration-200"
                  >
                    {item.label}
                  </button>
                )
              ))}
              
              <Button asChild variant="outline" className="w-full border-gray-600 text-gray-700 hover:bg-brand-secondary hover:text-white">
                <Link to="/app/login">
                  <LogIn className="mr-2 h-4 w-4" />
                  Login
                </Link>
              </Button>
              
              <div className="pt-4">
                <Button 
                  className="w-full bg-moss-green hover:bg-moss-green/90 text-white font-semibold hover-scale animate-pulse" 
                  onClick={() => handleScrollToSection('precos')}
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  Gerar Mapa
                </Button>
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default LP1790Header;
